import { useState, useCallback, useRef } from 'react';
import { WorkLogEntry, DateRange } from '../types';
import { JiraService } from '../../services/jira';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500] as const;

export const useWorkLogData = (jiraService: JiraService) => {
  const [data, setData] = useState<WorkLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tableEntries, setTableEntries] = useState<WorkLogEntry[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [tableLoading, setTableLoading] = useState(false);

  // Monotonically increasing request ID — each new call bumps it; stale responses are discarded.
  const dataRequestIdRef = useRef(0);

  // Загрузка одной страницы для таблицы (newest first)
  const loadWorkLogPage = useCallback(
    async (page: number, dateRange?: DateRange) => {
      setTableLoading(true);
      setError(null);
      try {
        const dateParams =
          dateRange?.startDate && dateRange?.endDate
            ? {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                ...(dateRange.fixVersion && String(dateRange.fixVersion).trim() !== ''
                  ? { fixVersion: String(dateRange.fixVersion).trim() }
                  : {}),
              }
            : undefined;
        const { entries, total } = await jiraService.getWorkLogEntriesPaginated(
          tablePageSize,
          page,
          dateParams
        );
        setTableEntries(Array.isArray(entries) ? entries : []);
        setTableTotal(typeof total === 'number' ? total : 0);
        setTablePage(page);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load page');
      } finally {
        setTableLoading(false);
      }
    },
    [jiraService, tablePageSize]
  );

  // Загрузка данных по диапазону дат
  const loadDataByDateRange = useCallback(
    async (dateRange: DateRange) => {
      const requestId = ++dataRequestIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const workLogData = await jiraService.getWorkLogEntriesByDateRange(dateRange);
        if (requestId !== dataRequestIdRef.current) return;
        setData(Array.isArray(workLogData) ? workLogData : []);
      } catch (err) {
        if (requestId !== dataRequestIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        if (requestId === dataRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [jiraService]
  );

  /** Page size for full sync — avoids one giant /api/worklog response (timeouts / pooler drops). */
  const WORKLOG_PAGE_SIZE = 3000;
  const WORKLOG_MAX_PAGES = 250;

  // Загрузка всех данных (постранично с сервера)
  const loadAllData = useCallback(async () => {
    const requestId = ++dataRequestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const merged: WorkLogEntry[] = [];
      let page = 1;
      let total = Infinity;
      while (merged.length < total && page <= WORKLOG_MAX_PAGES) {
        if (requestId !== dataRequestIdRef.current) return;
        const { entries, total: t } = await jiraService.getWorkLogEntriesPaginated(
          WORKLOG_PAGE_SIZE,
          page
        );
        if (requestId !== dataRequestIdRef.current) return;
        total = typeof t === 'number' ? t : merged.length;
        const batch = Array.isArray(entries) ? entries : [];
        merged.push(...batch);
        if (batch.length === 0) break;
        page += 1;
        if (merged.length >= total) break;
      }
      if (requestId !== dataRequestIdRef.current) return;
      setData(merged);
    } catch (err) {
      if (requestId !== dataRequestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      if (requestId === dataRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [jiraService]);

  // Очистка данных
  const clearData = useCallback(() => {
    setData([]);
    setError(null);
  }, []);

  // Сохранение данных
  const saveData = useCallback(
    async (dataToSave: WorkLogEntry[]) => {
      try {
        await jiraService.saveWorkLogData(dataToSave);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save data');
      }
    },
    [jiraService]
  );

  // Восстановление данных
  const restoreData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const restoredData = await jiraService.restoreWorkLogData();
      setData(Array.isArray(restoredData) ? restoredData : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore data');
    } finally {
      setLoading(false);
    }
  }, [jiraService]);

  return {
    data,
    loading,
    error,
    loadDataByDateRange,
    loadAllData,
    clearData,
    saveData,
    restoreData,
    tableEntries,
    tableTotal,
    tablePage,
    tablePageSize,
    setTablePageSize,
    tablePageSizeOptions: PAGE_SIZE_OPTIONS,
    tableLoading,
    loadWorkLogPage,
  };
};
